import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Globe, Shield } from "lucide-react";

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg)" }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 mb-6 transition-colors"
          style={{ color: "var(--color-text-dim)" }}
        >
          <ArrowLeft className="w-5 h-5" />
          Back to News
        </button>

        <div className="rounded-xl p-8 md:p-12" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Globe className="w-12 h-12" style={{ color: "var(--color-accent)" }} />
              <h1 className="text-4xl md:text-5xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                GLOBAL<span style={{ color: "var(--color-accent)" }}>NEWS</span> HUB
              </h1>
            </div>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: "var(--color-text-muted)" }}>
              Your trusted source for real-time news from around the world
            </p>
          </div>

          {/* About Us */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
              About Us
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: "var(--color-text-muted)" }}>
              Welcome to Global News Hub, your premier destination for real-time news coverage from every corner
              of the globe. Based in Atlanta, Georgia, we are a dedicated team committed to keeping you informed
              with the latest headlines, breaking stories, and in-depth reporting from trusted international sources.
              Our platform brings together news from over 30 leading outlets worldwide, spanning categories from
              US Politics and Business to Science, Sports, and Weather — all in one place.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              What sets us apart is our commitment to delivering the news to you instantly, 24 hours a day,
              7 days a week. Our feeds are automatically updated around the clock, so you never miss a beat —
              whether it's a major political development in Washington, a tech breakthrough in Tokyo, or weather
              alerts across the United States. At Global News Hub, the world's stories come to you the moment
              they happen, no delays, no waiting. Stay connected, stay informed, stay ahead.
            </p>
          </div>

          {/* Join the Conversation */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Join the Conversation
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              At Global News Hub, you can do more than just read the news — you can be part of the conversation.
              Registered users are welcome to politely comment on articles, reply to other readers, and like
              the comments they find insightful or helpful. We encourage respectful and thoughtful dialogue
              that enriches the experience for everyone in our community.
            </p>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Contact Us
            </h2>
            <div className="rounded-lg p-8 space-y-5" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-accent)" }}>
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: "var(--color-text)" }}>Email</p>
                  <a href="mailto:info@globalnewshub.us" className="text-lg font-bold underline transition-colors" style={{ color: "var(--color-accent)" }}>
                    info@globalnewshub.us
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-live)" }}>
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: "var(--color-text)" }}>Location</p>
                  <p className="text-lg font-bold" style={{ color: "var(--color-text)" }}>Atlanta, Georgia, United States</p>
                </div>
              </div>
            </div>
          </div>

          {/* News Categories */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
              News Categories
            </h2>
            <div className="flex flex-wrap gap-3">
              {["US Politics", "General News", "Business", "Technology", "Sports", "Entertainment",
                "Health", "Science", "US Weather"].map((category) => (
                <span key={category} className="px-4 py-2 text-white rounded-full font-semibold" style={{ backgroundColor: "var(--color-accent)" }}>
                  {category}
                </span>
              ))}
            </div>
          </div>

          {/* Our Commitment */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Our Commitment
            </h2>
            <div className="rounded-lg p-6" style={{ backgroundColor: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}>
              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 flex-shrink-0 mt-1" style={{ color: "var(--color-accent)" }} />
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: "var(--color-text)" }}>Transparency & Trust</h3>
                  <p className="leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                    We aggregate news from verified, reputable sources and clearly attribute all content to
                    the original publishers. We don't create or modify news content—we provide a platform
                    for you to discover and access quality journalism from trusted outlets worldwide.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
              How It Works
            </h2>
            <div className="space-y-4">
              {[
                { num: "1", title: "Real-Time Aggregation", desc: "Our platform automatically collects news from RSS feeds of major news organizations every few minutes." },
                { num: "2", title: "Smart Categorization", desc: "Articles are automatically organized by category and source, making it easy to find the news you care about." },
                { num: "3", title: "Personalized Experience", desc: "Create an account to customize your news feed, set notification preferences, and engage with the community." },
              ].map((step) => (
                <div key={step.num} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: "var(--color-accent)" }}>
                    {step.num}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ color: "var(--color-text)" }}>{step.title}</h3>
                    <p style={{ color: "var(--color-text-muted)" }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-12 text-center text-sm" style={{ color: "var(--color-text-dim)" }}>
            <p>© 2026 Global News Hub. All news articles belong to their respective publishers.</p>
            <p className="mt-2">We aggregate news from trusted sources worldwide. All content remains the property of the original publishers.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
